import type { ActResult, CasinoGame, TableEnv } from './types';
import { STEP_BASIS, luckOf, luckTarget, stepSaveRate } from './luck';

/**
 * Tower: climb 8 rows, picking one of three doors per row. On Easy one door
 * per row is a trap; on Hard two are. Each safe row raises the payout; a
 * trap loses the bet. Cash out any time.
 *
 * Like Mines, the game takes over the gestures while climbing: scroll moves
 * between the three doors, tap opens one, double-tap cashes out. Payout
 * after k rows is the fair odds of surviving k rows times (1 - EDGE), so
 * every stopping point has the same edge.
 */
export const ROWS = 8;
export const DOORS = 3;
export const EDGE = 0.03;

export type TowerMode = 'easy' | 'hard';
const SAFE_CHANCE: Record<TowerMode, number> = { easy: 2 / 3, hard: 1 / 3 };

export function towerMultiplier(mode: TowerMode, rows: number, edge: number = EDGE): number {
  return rows === 0 ? 1 : (1 - edge) / SAFE_CHANCE[mode] ** rows;
}

export interface TowerState {
  phase: 'idle' | 'climbing' | 'done';
  mode: TowerMode;
  /** Trap doors per row (0-2), fixed when the climb starts. */
  traps: number[][];
  /** Door picked on each cleared row. */
  picks: number[];
  cursor: number;
  bet: number;
  edge: number;
  /** Row that caught you, if any. */
  fell: number | null;
  /** The table's luck when the climb started (★ luck makes traps give way). */
  luck?: number;
  /** A trap gave way this climb. */
  dud?: boolean;
  returned: number;
}

function payout(state: TowerState, rows: number): number {
  return Math.round(state.bet * towerMultiplier(state.mode, rows, state.edge));
}

function cashOut(state: TowerState, bankroll: number): ActResult<TowerState> {
  const rows = state.picks.length;
  const returned = payout(state, rows);
  return {
    state: { ...state, phase: 'done', returned },
    bankroll: bankroll + returned,
    // Cashing out before any door risked nothing: not a play (see highLow).
    settled: rows === 0 ? undefined : { wagered: state.bet, returned },
    jackpot: state.mode === 'hard' && rows === ROWS,
  };
}

function open(state: TowerState, env: TableEnv): ActResult<TowerState> {
  const row = state.picks.length;
  if (state.traps[row]!.includes(state.cursor)) {
    // The ★'s luck: sometimes the trap is behind another door after all.
    const safeDoors = [0, 1, 2].filter((d) => !state.traps[row]!.includes(d));
    const ratio = Math.sqrt(luckTarget(state.luck ?? 0) / (1 - (state.edge ?? EDGE)));
    // No luck, no saves (a normal table); see Mines.
    const q = (state.luck ?? 0) > 0 ? stepSaveRate(safeDoors.length / 3, ratio ** (2 / STEP_BASIS)) : 0;
    if (env.rng.next() < q) {
      const to = safeDoors[env.rng.int(safeDoors.length)]!;
      const traps = state.traps.map((r, i) => (i === row ? r.map((d) => (d === state.cursor ? to : d)) : r));
      // The trap door creaks... and holds.
      const safe = open({ ...state, traps, dud: true }, env);
      const creak = drawTower({ ...state, phase: 'done', cursor: -1 }, { row, door: state.cursor, char: '!' });
      return { ...safe, frames: [`${creak}\n\nCreak...`, `${creak}\n\n★ The trap gives way!`, ...(safe.frames ?? [])] };
    }
    // The floor gives way and you drop down the tower.
    const falling = { ...state, phase: 'climbing' as const, cursor: -1 };
    const fall = Array.from({ length: row + 1 }, (_, k) => `${drawTower(falling, { row: row - k, door: state.cursor, char: 'v' })}\nFalling...`);
    return {
      state: { ...state, phase: 'done', fell: row, returned: 0 },
      bankroll: env.bankroll,
      settled: { wagered: state.bet, returned: 0 },
      frames: fall,
    };
  }
  const next: TowerState = { ...state, picks: [...state.picks, state.cursor], cursor: 1 };
  // Top of the tower: nothing left to risk.
  return next.picks.length === ROWS ? cashOut(next, env.bankroll) : { state: next, bankroll: env.bankroll };
}

/** The tower, top row first. The row you're on shows the door cursor. */
/** The tower; `at` marks one cell (for animations). */
function drawTower(state: TowerState, at?: { row: number; door: number; char: string }): string {
  const lines: string[] = [];
  const current = state.picks.length;
  for (let row = ROWS - 1; row >= 0; row--) {
    const cells: string[] = [];
    for (let door = 0; door < DOORS; door++) {
      let shown = '·';
      if (row < current) shown = state.picks[row] === door ? '$' : '·';
      if (state.phase === 'done' && row >= current && state.traps[row]!.includes(door)) shown = 'x';
      if (state.fell === row && state.cursor === door) shown = 'X';
      if (at && at.row === row && at.door === door) shown = at.char;
      const selected = state.phase === 'climbing' && row === current && state.cursor === door;
      cells.push(selected ? `[${shown}]` : ` ${shown} `);
    }
    lines.push(`  ${cells.join(' ')}`);
  }
  return lines.join('\n');
}

export const tower: CasinoGame<TowerState> = {
  id: 'tower',
  noDealer: true,
  usesLuck: true,
  name: 'Tower',
  blurb: 'Climb 8 rows, one door of three each. Cash out any time.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: now and then a trap gives way.',
  jackpotRule: 'Reach the top on Hard.',
  lockedHint: '• open door  •• cash out',
  create: () => ({ phase: 'idle', mode: 'easy', traps: [], picks: [], cursor: 1, bet: 0, edge: EDGE, fell: null, returned: 0 }),
  locked: (state) => state.phase === 'climbing',
  options: (state) => [
    { id: 'start', label: state.phase === 'done' ? 'Climb again' : 'Start climbing' },
    { id: 'mode', label: `Mode: ${state.mode === 'easy' ? 'Easy (1 trap)' : 'Hard (2 traps)'}` },
  ],
  describe(state, env, highlighted) {
    const edge = state.phase === 'idle' ? EDGE : state.edge;
    if (highlighted === 'mode' && state.phase !== 'climbing') {
      return `Easy: 1 trap per row.\nHard: 2 traps per row.\n\nTop on this mode: x${towerMultiplier(state.mode, ROWS, edge).toFixed(1)}\n\nTap to change.`;
    }
    if (state.phase === 'climbing') {
      const rows = state.picks.length;
      const now = rows === 0 ? 'Cash out: bet back' : `Cash out: $${payout(state, rows)}`;
      return `Row ${rows + 1} of ${ROWS}\n${now}\nNext: x${towerMultiplier(state.mode, rows + 1, edge).toFixed(2)} ($${payout(state, rows + 1)})`;
    }
    if (state.phase === 'done') {
      const net = state.returned - state.bet;
      const result = state.fell !== null ? `Trap on row ${state.fell + 1}. -$${state.bet}` : net > 0 ? `${state.dud ? '★ ' : ''}Cashed out +$${net}` : 'Cashed out, broke even';
      // Not the full tower: 8 rows won't fit beside a result and notices.
      return `${result}\nCleared ${state.picks.length} of ${ROWS} rows (${state.mode === 'easy' ? 'Easy' : 'Hard'})`;
    }
    return 'Pick a door on each row. Find the traps and the bet is gone.';
  },
  act(state, optionId, env) {
    if (state.phase === 'climbing') return { state, bankroll: env.bankroll };
    if (optionId === 'mode') return { state: { ...state, mode: state.mode === 'easy' ? 'hard' : 'easy' }, bankroll: env.bankroll };
    if (optionId !== 'start') return { state, bankroll: env.bankroll };
    const trapsPerRow = state.mode === 'easy' ? 1 : 2;
    const traps = Array.from({ length: ROWS }, () => env.rng.shuffle([0, 1, 2]).slice(0, trapsPerRow));
    return {
      state: { ...state, phase: 'climbing', traps, picks: [], cursor: 1, bet: env.bet, edge: EDGE, luck: luckOf(env), dud: false, fell: null, returned: 0 },
      bankroll: env.bankroll - env.bet,
    };
  },
  gesture(state, gesture, env) {
    if (state.phase !== 'climbing') return null;
    if (gesture === 'up' || gesture === 'down') {
      const step = gesture === 'up' ? -1 : 1;
      return { state: { ...state, cursor: (state.cursor + step + DOORS) % DOORS }, bankroll: env.bankroll };
    }
    if (gesture === 'tap') return open(state, env);
    return cashOut(state, env.bankroll);
  },
  leftPane: (state) => (state.phase === 'climbing' ? drawTower(state) : null),
};
